/**
 * Animation Handler - Orchestrates animation playback and transitions.
 *
 * This module handles:
 * - Animation frame rendering
 * - Transition playback from idle to animation and back
 * - Frame timing at 25fps
 * - Session state tracking
 *
 * The handler relies on server-sent packet flags (Transition, TransitionEnd, Idle)
 * to determine when to generate transitions, rather than maintaining complex internal state.
 *
 * @internal
 * @packageDocumentation
 */

import { logger, trackEvent } from '../utils';

/**
 * Opaque animation frame data.
 * The actual shape is defined in web-sdk (KeyframeData).
 * rtc-sdk treats these as opaque — it only stores and passes them to the renderer.
 * @internal
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnimationFrame = any;

/**
 * Interface that applications must implement to render avatar frames.
 * The SDK calls these methods to control the avatar.
 * @internal
 */
export interface AvatarRenderer {
  /**
   * Render a single animation frame from raw protobuf data.
   * The renderer decodes the protobuf internally.
   * @param protobufData - Raw protobuf bytes containing animation keyframes
   */
  renderFromProtobuf(protobufData: ArrayBuffer): void;

  /**
   * Render a pre-decoded animation frame (e.g., transition frame).
   * @param frame - Opaque frame data, or undefined to render idle
   * @param startIdle - If true and frame is undefined, start idle animation loop
   */
  renderFrame(frame: AnimationFrame | undefined, startIdle?: boolean): void;

  /**
   * Generate transition frames from idle to the target in the protobuf data.
   * The renderer decodes the protobuf and generates interpolated frames internally.
   * @param protobufData - Raw protobuf bytes containing the target frame
   * @param frameCount - Number of transition frames to generate
   * @returns Promise resolving to array of opaque transition frames
   */
  generateTransitionFromProtobuf(
    protobufData: ArrayBuffer,
    frameCount: number,
  ): Promise<AnimationFrame[]>;

  /**
   * Check if the renderer is available and ready.
   * @returns true if renderer is ready to accept frames
   */
  isReady(): boolean;
}

/**
 * Configuration for AnimationHandler.
 * @internal
 */
export interface AnimationHandlerConfig {
  /**
   * Number of transition frames when starting animation.
   * Default: 8 (~320ms at 25fps)
   */
  transitionStartFrameCount?: number;

  /**
   * Number of transition frames when ending animation.
   * Default: 12 (~480ms at 25fps)
   */
  transitionEndFrameCount?: number;

  /**
   * Callback when data stream stalls and fallback to idle is triggered.
   * Called when no animation frames received for 5 seconds during speaking session.
   */
  onStreamStalled?: () => void;

  /**
   * Enable jitter buffer for smoother playback.
   * When enabled, frames are buffered and rendered in sequence order at a steady 25fps,
   * absorbing network jitter and out-of-order delivery.
   * Default: true
   */
  enableJitterBuffer?: boolean;

  /**
   * Maximum delay (in ms) a frame can sit in the jitter buffer before being rendered.
   * Also controls how long to wait for a missing frame before skipping ahead.
   * Default: 80 (2 frames at 25fps)
   */
  maxBufferDelayMs?: number;

  /**
   * Provider name for telemetry.
   */
  providerName?: string;
}

/**
 * Jitter buffer state machine.
 * @internal
 */
type BufferState = 'direct' | 'filling' | 'draining' | 'starved';

/**
 * A frame held in the jitter buffer awaiting playback.
 * @internal
 */
interface BufferedFrame {
  protobufData: ArrayBuffer;
  seq: number;
  receivedAt: number;
}

/**
 * AnimationHandler manages all animation playback and transition logic.
 *
 * @internal
 * This class is used internally by AvatarPlayer.
 * Applications should not instantiate this class directly.
 */
export class AnimationHandler {
  /** @internal */
  private renderer: AvatarRenderer;
  /** @internal */
  private config: Required<
    Omit<AnimationHandlerConfig, 'onStreamStalled' | 'providerName'>
  >;

  // Frame tracking
  /** @internal */
  private animationFrameCount = 0;
  /** @internal */
  private lastRenderedFrameSeq = -1;
  /** @internal */
  private renderedFrameCount = 0;

  // Transition state
  /** @internal */
  private isPlayingTransition = false;
  /** @internal */
  private isTransitioningToIdle = false;
  /** @internal */
  private transitionFrames: AnimationFrame[] = [];
  /** @internal */
  private transitionFrameIndex = 0;
  /** @internal */
  private transitionTimeoutId: ReturnType<typeof setTimeout> | null = null;

  // Guards against race conditions during async transition generation
  /** @internal */
  private isGeneratingStartTransition = false;
  /** @internal */
  private isGeneratingEndTransition = false;

  // Session-level flags to prevent duplicate handling
  // These are reset in resetTracking() for new sessions
  /** @internal */
  private hasHandledTransitionStart = false;
  /** @internal */
  private hasHandledTransitionEnd = false;

  // Watchdog for detecting stalled data stream
  /** @internal */
  private lastFrameReceivedTime = 0;
  /** @internal */
  private isInSession = false;
  /** @internal */
  private watchdogTimer: ReturnType<typeof setInterval> | null = null;
  /** @internal */
  private hasReportedStall = false;
  /** @internal */
  private static readonly STALL_TIMEOUT_MS = 5000;
  /** @internal */
  private onStreamStalledCallback: (() => void) | null = null;
  /** @internal */
  private providerName: string = '';
  /** @internal */
  private isStalledFallback = false; // True when fallback to idle was triggered due to stall

  // Playback stats
  /** @internal */
  private playbackStatsTimer: ReturnType<typeof setInterval> | null = null;
  /** @internal */
  private playbackFrameCount = 0;
  /** @internal */
  private playbackFrameTimestamps: number[] = [];
  /** @internal */
  private playbackGapCount = 0;
  /** @internal */
  private playbackExpectedSeq = -1;

  // Jitter buffer
  /** @internal */
  private bufferState: BufferState = 'direct';
  /** @internal */
  private frameBuffer = new Map<number, BufferedFrame>();
  /** @internal */
  private bufferNextSeq = -1;
  /** @internal */
  private bufferDrainTimer: ReturnType<typeof setTimeout> | null = null;
  /** @internal */
  private bufferLastDrainTime = 0;
  /** @internal */
  private static readonly BUFFER_MAX_SIZE = 4;
  /** @internal */
  private static readonly BUFFER_INITIAL_FILL = 2;
  /** @internal */
  private static readonly BUFFER_FRAME_INTERVAL_MS = 40;

  // Cumulative session stats (never reset until getSessionSummary)
  /** @internal */
  private cumulativeTotalFrames = 0;
  /** @internal */
  private cumulativeLost = 0;
  /** @internal */
  private cumulativeRecovered = 0;
  /** @internal */
  private cumulativeDropped = 0;
  /** @internal */
  private cumulativeFpsReadings: number[] = [];

  // Per-conversation stats for rtc_playback_stats
  /** @internal */
  private conversationFrameCount = 0;
  /** @internal */
  private conversationLost = 0;
  /** @internal */
  private conversationRecovered = 0;
  /** @internal */
  private conversationDropped = 0;
  /** @internal */
  private conversationFpsReadings: number[] = [];
  /** @internal */
  private conversationStartTime = 0;

  /**
   * @internal
   */
  constructor(renderer: AvatarRenderer, config: AnimationHandlerConfig = {}) {
    this.renderer = renderer;
    this.config = {
      transitionStartFrameCount: config.transitionStartFrameCount ?? 8,
      transitionEndFrameCount: config.transitionEndFrameCount ?? 12,
      enableJitterBuffer: config.enableJitterBuffer ?? true,
      maxBufferDelayMs: config.maxBufferDelayMs ?? 80,
    };
    this.onStreamStalledCallback = config.onStreamStalled ?? null;
    this.providerName = config.providerName ?? '';
  }

  /**
   * Handle animation data received from RTC provider.
   * @param protobufData - Raw protobuf bytes
   * @param frameSeq - Frame sequence number (optional)
   * @param isRecovered - Whether this frame was recovered via ALR
   * @internal
   */
  handleAnimationData(
    protobufData: ArrayBuffer,
    frameSeq?: number,
    isRecovered?: boolean,
  ): void {
    // If we were in transition, stop it and play normal animation
    if (this.isPlayingTransition) {
      this.stopTransition();
    }

    // Update watchdog state
    const now = Date.now();
    if (this.hasReportedStall) {
      // Recovered from stall
      const stallDuration = now - this.lastFrameReceivedTime;
      logger.info(
        'AnimationHandler',
        `Data stream resumed after ${stallDuration}ms stall`,
      );
      this.hasReportedStall = false;
    }
    // If we were in stalled fallback, clear the flag (data has resumed, no transition needed)
    if (this.isStalledFallback) {
      logger.info(
        'AnimationHandler',
        'Resuming from stall fallback, rendering directly without transition',
      );
      this.isStalledFallback = false;
    }
    this.lastFrameReceivedTime = now;

    this.animationFrameCount++;

    // In lossy networks the transition packet may be delayed/lost.
    // Ensure session-level watchdog/stats still start on first valid playback frame.
    this.ensureSessionActive(frameSeq);

    // Jitter buffer path: insert into buffer instead of rendering directly
    if (this.config.enableJitterBuffer && frameSeq !== undefined) {
      this.bufferFrame(protobufData, frameSeq);
      return;
    }

    // Direct path (no buffer)
    // Check for out-of-order delivery
    if (frameSeq !== undefined && this.lastRenderedFrameSeq !== -1) {
      if (frameSeq < this.lastRenderedFrameSeq) {
        // Out-of-order frame - discard (already rendered a later frame)
        logger.warn(
          'AnimationHandler',
          `OUT-OF-ORDER: seq=${frameSeq}, lastRendered=${this.lastRenderedFrameSeq}${isRecovered ? ' [RECOVERED]' : ''}, discarding`,
        );
        this.conversationDropped++;
        return;
      } else if (frameSeq === this.lastRenderedFrameSeq) {
        // Duplicate frame - skip silently (expected when server sends redundant frames)
        return;
      } else if (frameSeq > this.lastRenderedFrameSeq + 1) {
        const gap = frameSeq - this.lastRenderedFrameSeq - 1;
        logger.warn(
          'AnimationHandler',
          `GAP: ${gap} frame(s) between ${this.lastRenderedFrameSeq} and ${frameSeq}${isRecovered ? ' [RECOVERED]' : ''}`,
        );
      }
    }

    if (frameSeq !== undefined) {
      this.lastRenderedFrameSeq = frameSeq;
    }

    // Render directly (decoding happens inside the renderer)
    this.renderedFrameCount++;
    if (isRecovered) this.conversationRecovered++;
    this.renderer.renderFromProtobuf(protobufData);
    this.logRenderedFrame('direct', frameSeq, isRecovered);

    // Collect playback stats
    this.playbackFrameTimestamps.push(performance.now());
    this.playbackFrameCount++;
    if (frameSeq !== undefined) {
      if (
        this.playbackExpectedSeq >= 0 &&
        frameSeq > this.playbackExpectedSeq
      ) {
        this.playbackGapCount += frameSeq - this.playbackExpectedSeq;
      }
      this.playbackExpectedSeq = frameSeq + 1;
    }
  }

  /**
   * Handle transition packet - generate and play transition from idle to target.
   * Only starts transition on the first packet; subsequent packets are ignored while transitioning.
   * @param protobufData - Protobuf data containing target frame
   * @param frameCount - Number of transition frames (overrides config if provided)
   * @internal
   */
  async handleTransitionData(
    protobufData: ArrayBuffer,
    frameCount?: number,
  ): Promise<void> {
    logger.info(
      'AnimationHandler',
      `Start transition packet received (bytes=${protobufData.byteLength}, requestedFrames=${frameCount ?? this.config.transitionStartFrameCount}, hasHandledStart=${this.hasHandledTransitionStart}, isInSession=${this.isInSession}, isPlayingTransition=${this.isPlayingTransition}, isGeneratingStart=${this.isGeneratingStartTransition}, lastRenderedSeq=${this.lastRenderedFrameSeq}, bufferState=${this.bufferState}, buffered=${this.frameBuffer.size})`,
    );

    // Ignore if we've already handled a transition start for this session
    if (this.hasHandledTransitionStart) {
      return;
    }
    // Ignore if already playing or generating a start transition
    if (this.isPlayingTransition && !this.isTransitioningToIdle) {
      return;
    }
    if (this.isGeneratingStartTransition) {
      return;
    }

    if (!this.renderer.isReady()) {
      logger.warn('AnimationHandler', 'Renderer not ready for transition');
      return;
    }

    // Once streaming playback has started, start-transition packets are stale.
    // Ignore them to avoid jumping back into transition and causing visual jitter.
    if (
      this.isInSession &&
      (this.lastRenderedFrameSeq >= 0 ||
        this.frameBuffer.size > 0 ||
        this.bufferState !== 'direct')
    ) {
      this.hasHandledTransitionStart = true;
      logger.warn(
        'AnimationHandler',
        `Ignoring late transition packet after playback start (lastRenderedSeq=${this.lastRenderedFrameSeq}, bufferState=${this.bufferState}, buffered=${this.frameBuffer.size})`,
      );
      return;
    }

    // Mark that we've handled transition start for this session
    this.hasHandledTransitionStart = true;
    // Reset transition end flag since we're starting a new transition
    this.hasHandledTransitionEnd = false;

    // Start session and watchdog
    this.ensureSessionActive();

    const frames = frameCount ?? this.config.transitionStartFrameCount;
    logger.info(
      'AnimationHandler',
      `Generating ${frames} transition frames to target`,
    );

    // Set guard flag before async operation
    this.isGeneratingStartTransition = true;

    try {
      // Generate transition frames (renderer decodes protobuf internally)
      const transitionFrames = await this.renderer.generateTransitionFromProtobuf(
        protobufData,
        frames,
      );
      logger.info(
        'AnimationHandler',
        `Generated ${transitionFrames.length} transition frames`,
      );

      // Start playing transition frames at 25fps
      this.isPlayingTransition = true;
      this.isTransitioningToIdle = false;
      this.transitionFrames = transitionFrames;
      this.transitionFrameIndex = 0;

      this.playTransitionFrame();
    } catch (error) {
      logger.error('AnimationHandler', 'Failed to generate transition:', error);
      // Fallback: render protobuf directly
      this.renderer.renderFromProtobuf(protobufData);
      this.logRenderedFrame('transition-fallback');
    } finally {
      this.isGeneratingStartTransition = false;
    }
  }

  /**
   * Handle transition end - generate and play reverse transition back to idle.
   * Only starts transition on the first packet; subsequent packets are ignored while transitioning.
   * @param protobufData - Protobuf data containing last animation frame
   * @param frameCount - Number of transition frames (overrides config if provided)
   * @internal
   */
  async handleTransitionToIdle(
    protobufData: ArrayBuffer,
    frameCount?: number,
  ): Promise<void> {
    // Ignore stale transition-end packets after session has already returned to idle.
    if (!this.isInSession) {
      logger.info(
        'AnimationHandler',
        'Ignoring transition end packet with no active session',
      );
      return;
    }

    // Ignore if we've already handled a transition end for this session
    if (this.hasHandledTransitionEnd) {
      return;
    }
    // Ignore if already playing or generating an end transition
    if (this.isPlayingTransition && this.isTransitioningToIdle) {
      return;
    }
    if (this.isGeneratingEndTransition) {
      return;
    }

    if (!this.renderer.isReady()) {
      logger.warn(
        'AnimationHandler',
        'Renderer not ready for transition to idle',
      );
      this.renderer.renderFrame(undefined, true);
      this.logRenderedFrame('idle');
      return;
    }

    // Mark that we've handled transition end for this session
    this.hasHandledTransitionEnd = true;

    // Drop any queued speaking frames so they cannot interleave with end transition playback.
    this.flushBuffer();

    const frames = frameCount ?? this.config.transitionEndFrameCount;
    logger.info(
      'AnimationHandler',
      `Generating ${frames} reverse transition frames to idle`,
    );

    // Set guard flag before async operation
    this.isGeneratingEndTransition = true;

    try {
      // Generate transition frames (renderer decodes protobuf internally), then reverse
      const transitionFrames = await this.renderer.generateTransitionFromProtobuf(
        protobufData,
        frames,
      );
      logger.info(
        'AnimationHandler',
        `Generated ${transitionFrames.length} transition frames, reversing for playback`,
      );

      // Reverse frames to play from last animation frame back to idle
      const reversedFrames = transitionFrames.slice().reverse();

      // Start playing reversed transition at 25fps
      this.isPlayingTransition = true;
      this.isTransitioningToIdle = true;
      this.transitionFrames = reversedFrames;
      this.transitionFrameIndex = 0;

      this.playTransitionFrame();
    } catch (error) {
      logger.error(
        'AnimationHandler',
        'Failed to generate reverse transition:',
        error,
      );
      this.renderer.renderFrame(undefined, true);
      this.logRenderedFrame('idle');
    } finally {
      this.isGeneratingEndTransition = false;
    }
  }

  /**
   * Start idle animation.
   * @internal
   */
  startIdle(): void {
    this.reportConversationStats();
    this.isInSession = false;
    this.hasReportedStall = false;
    this.renderer.renderFrame(undefined, true);
    this.logRenderedFrame('idle');
  }

  /**
   * Ensure session-level timers/stats are active.
   * @internal
   */
  private ensureSessionActive(frameSeq?: number): void {
    if (this.isInSession) {
      return;
    }

    this.isInSession = true;
    this.lastFrameReceivedTime = Date.now();
    this.hasReportedStall = false;
    if (!this.conversationStartTime) {
      this.conversationStartTime = Date.now();
    }
    this.startWatchdog();
    this.startPlaybackStats();

    if (frameSeq !== undefined) {
      logger.info(
        'AnimationHandler',
        `Session started from animation frame seq=${frameSeq}`,
      );
    }
  }

  /**
   * Reset animation frame tracking (call on session start).
   * @internal
   */
  resetTracking(): void {
    this.lastRenderedFrameSeq = -1;
    this.renderedFrameCount = 0;
    this.animationFrameCount = 0;
    // Reset session-level transition flags for new session
    this.hasHandledTransitionStart = false;
    this.hasHandledTransitionEnd = false;
    this.resetPlaybackStats();
    this.flushBuffer();
    logger.info('AnimationHandler', 'Frame tracking reset');
  }

  /**
   * Check if currently playing transition frames.
   * @internal
   */
  isInTransition(): boolean {
    return (
      this.isPlayingTransition ||
      this.isGeneratingStartTransition ||
      this.isGeneratingEndTransition
    );
  }

  /**
   * Stop transition playback.
   * @internal
   */
  stopTransition(): void {
    if (
      this.isPlayingTransition ||
      this.isGeneratingStartTransition ||
      this.isGeneratingEndTransition
    ) {
      logger.info('AnimationHandler', 'Stopping transition playback');
    }
    this.isPlayingTransition = false;
    this.isTransitioningToIdle = false;
    this.isGeneratingStartTransition = false;
    this.isGeneratingEndTransition = false;
    // Note: We intentionally do NOT reset hasHandledTransitionStart/End here
    // Those are session-level flags, only reset in resetTracking()
    this.transitionFrames = [];
    this.transitionFrameIndex = 0;
    if (this.transitionTimeoutId !== null) {
      clearTimeout(this.transitionTimeoutId);
      this.transitionTimeoutId = null;
    }
    this.flushBuffer();
  }

  /**
   * Get cumulative session summary for telemetry reporting.
   * @internal
   */
  getSessionSummary(): {
    totalFrames: number;
    totalLost: number;
    totalRecovered: number;
    totalDropped: number;
    avgFps: number;
  } {
    const avgFps =
      this.cumulativeFpsReadings.length > 0
        ? Math.round(
            this.cumulativeFpsReadings.reduce((a, b) => a + b, 0) /
              this.cumulativeFpsReadings.length,
          )
        : 0;

    return {
      totalFrames: this.cumulativeTotalFrames,
      totalLost: this.cumulativeLost,
      totalRecovered: this.cumulativeRecovered,
      totalDropped: this.cumulativeDropped,
      avgFps,
    };
  }

  /**
   * Report per-conversation playback stats via telemetry.
   * Called when a conversation ends (transition to idle).
   * @internal
   */
  private reportConversationStats(): void {
    if (this.conversationFrameCount === 0) return;

    const durationMs = this.conversationStartTime
      ? Date.now() - this.conversationStartTime
      : 0;
    const avgFps =
      this.conversationFpsReadings.length > 0
        ? Math.round(
            this.conversationFpsReadings.reduce((a, b) => a + b, 0) /
              this.conversationFpsReadings.length,
          )
        : 0;
    const totalExpected = this.conversationFrameCount + this.conversationLost;
    const lossRate =
      totalExpected > 0
        ? Number(((this.conversationLost / totalExpected) * 100).toFixed(1))
        : 0;

    trackEvent('rtc_playback_stats', 'info', {
      provider: this.providerName,
      avg_fps: avgFps,
      frame_count: this.conversationFrameCount,
      frames_lost: this.conversationLost,
      frames_recovered: this.conversationRecovered,
      frames_dropped: this.conversationDropped,
      loss_rate: lossRate,
      duration_ms: durationMs,
    });

    // Accumulate into session-level stats
    this.cumulativeTotalFrames += this.conversationFrameCount;
    this.cumulativeLost += this.conversationLost;
    this.cumulativeRecovered += this.conversationRecovered;
    this.cumulativeDropped += this.conversationDropped;
    this.cumulativeFpsReadings.push(...this.conversationFpsReadings);

    // Reset per-conversation counters
    this.conversationFrameCount = 0;
    this.conversationLost = 0;
    this.conversationRecovered = 0;
    this.conversationDropped = 0;
    this.conversationFpsReadings = [];
    this.conversationStartTime = 0;
  }

  /**
   * Clean up resources.
   * @internal
   */
  dispose(): void {
    this.stopTransition();
    this.stopWatchdog();
  }

  /**
   * Start the watchdog timer to detect stalled data streams.
   * @internal
   */
  private startWatchdog(): void {
    if (this.watchdogTimer) {
      return; // Already running
    }

    this.watchdogTimer = setInterval(() => {
      if (!this.isInSession) {
        return;
      }

      // Don't check during transition playback (we're playing generated frames, not receiving)
      if (this.isPlayingTransition) {
        return;
      }

      const elapsed = Date.now() - this.lastFrameReceivedTime;
      if (
        elapsed > AnimationHandler.STALL_TIMEOUT_MS &&
        !this.hasReportedStall
      ) {
        logger.error(
          'AnimationHandler',
          `Data stream stalled: no frames received for ${elapsed}ms, falling back to idle`,
        );
        this.hasReportedStall = true;
        this.isStalledFallback = true;

        // Trigger fallback to idle
        this.startIdle();

        // Notify external listener
        if (this.onStreamStalledCallback) {
          try {
            this.onStreamStalledCallback();
          } catch (e) {
            logger.error(
              'AnimationHandler',
              'Error in onStreamStalled callback:',
              e,
            );
          }
        }
      }
    }, 1000); // Check every 1 second
  }

  /**
   * Stop the watchdog timer.
   * @internal
   */
  private stopWatchdog(): void {
    if (this.watchdogTimer) {
      clearInterval(this.watchdogTimer);
      this.watchdogTimer = null;
    }
    this.hasReportedStall = false;
    this.isStalledFallback = false;
    this.stopPlaybackStats();
  }

  /**
   * Start the playback stats reporting timer.
   * @internal
   */
  private startPlaybackStats(): void {
    if (this.playbackStatsTimer) {
      return; // Already running
    }
    this.resetPlaybackStats();
    this.playbackStatsTimer = setInterval(() => {
      this.reportPlaybackStats();
    }, 1000);
  }

  /**
   * Stop the playback stats reporting timer.
   * @internal
   */
  private stopPlaybackStats(): void {
    if (this.playbackStatsTimer) {
      clearInterval(this.playbackStatsTimer);
      this.playbackStatsTimer = null;
    }
    this.resetPlaybackStats();
  }

  /**
   * Reset playback stats counters.
   * @internal
   */
  private resetPlaybackStats(): void {
    this.playbackFrameCount = 0;
    this.playbackFrameTimestamps = [];
    this.playbackGapCount = 0;
    this.playbackExpectedSeq = -1;
  }

  /**
   * Report playback stats (called every 1s by timer).
   * Logs FPS, frame loss rate, and playback jitter.
   * @internal
   */
  private reportPlaybackStats(): void {
    // Skip reporting during transitions (locally generated frames)
    if (this.isPlayingTransition) {
      this.resetPlaybackStats();
      return;
    }

    // Skip if no frames were rendered this interval
    if (this.playbackFrameCount === 0) {
      return;
    }

    const fps = this.playbackFrameCount;
    const totalExpected = this.playbackFrameCount + this.playbackGapCount;
    const lossRate =
      totalExpected > 0 ? (this.playbackGapCount / totalExpected) * 100 : 0;

    // Calculate jitter: std deviation of inter-frame intervals
    let jitter = 0;
    if (this.playbackFrameTimestamps.length >= 2) {
      const intervals: number[] = [];
      for (let i = 1; i < this.playbackFrameTimestamps.length; i++) {
        intervals.push(
          this.playbackFrameTimestamps[i] - this.playbackFrameTimestamps[i - 1],
        );
      }
      const mean = intervals.reduce((a, b) => a + b, 0) / intervals.length;
      const variance =
        intervals.reduce((sum, v) => sum + (v - mean) ** 2, 0) /
        intervals.length;
      jitter = Math.sqrt(variance);
    }

    logger.info(
      'AnimationHandler',
      `Playback stats: fps=${fps}, lossRate=${lossRate.toFixed(1)}%, jitter=${jitter.toFixed(1)}ms`,
    );

    // Feed per-conversation counters
    this.conversationFrameCount += this.playbackFrameCount;
    this.conversationLost += this.playbackGapCount;
    this.conversationFpsReadings.push(fps);

    // Reset counters for next interval
    this.playbackFrameCount = 0;
    this.playbackFrameTimestamps = [];
    this.playbackGapCount = 0;
  }

  // ── Jitter Buffer ──

  /**
   * Insert a frame into the jitter buffer.
   * Handles dedup, enforces max size, and drives the buffer state machine.
   * @internal
   */
  private bufferFrame(protobufData: ArrayBuffer, seq: number): void {
    // Never allow frames older than what we've already rendered.
    if (this.lastRenderedFrameSeq >= 0 && seq <= this.lastRenderedFrameSeq) {
      logger.warn(
        'AnimationHandler',
        `Jitter buffer: dropping stale frame seq=${seq} (lastRendered=${this.lastRenderedFrameSeq})`,
      );
      this.conversationDropped++;
      return;
    }

    // If we're already waiting for a newer sequence, this frame arrived too late.
    if (this.bufferNextSeq >= 0 && seq < this.bufferNextSeq) {
      logger.warn(
        'AnimationHandler',
        `Jitter buffer: dropping late frame seq=${seq} (nextExpected=${this.bufferNextSeq})`,
      );
      this.conversationDropped++;
      return;
    }

    // Dedup — server sends each frame twice for resilience
    if (this.frameBuffer.has(seq)) {
      return;
    }

    this.frameBuffer.set(seq, { protobufData, seq, receivedAt: performance.now() });

    // Enforce max buffer size — drop oldest when full
    if (this.frameBuffer.size > AnimationHandler.BUFFER_MAX_SIZE) {
      let oldestSeq = Infinity;
      for (const k of this.frameBuffer.keys()) {
        if (k < oldestSeq) oldestSeq = k;
      }
      this.frameBuffer.delete(oldestSeq);
      this.conversationDropped++;
      logger.warn(
        'AnimationHandler',
        `Jitter buffer: overflow, dropping seq=${oldestSeq} (nextExpected=${this.bufferNextSeq})`,
      );
    }

    // State machine transitions
    switch (this.bufferState) {
      case 'direct':
        this.bufferState = 'filling';
        if (this.bufferNextSeq < 0) {
          this.bufferNextSeq = seq;
        }
        logger.info(
          'AnimationHandler',
          `Jitter buffer: filling (first frame seq=${seq})`,
        );
        if (this.frameBuffer.size >= AnimationHandler.BUFFER_INITIAL_FILL) {
          this.startBufferDrain();
        }
        break;
      case 'filling':
        if (this.frameBuffer.size >= AnimationHandler.BUFFER_INITIAL_FILL) {
          this.startBufferDrain();
        }
        break;
      case 'starved':
        this.startBufferDrain();
        break;
      case 'draining':
        // Already draining, frame will be picked up by drain loop
        break;
    }
  }

  /**
   * Drop buffered frames that are now too old to ever be rendered in-order.
   * @internal
   */
  private dropStaleBufferedFrames(): void {
    if (this.frameBuffer.size === 0) {
      return;
    }

    const minAllowedSeq = Math.max(
      this.bufferNextSeq,
      this.lastRenderedFrameSeq + 1,
    );
    if (minAllowedSeq < 0) {
      return;
    }

    let dropped = 0;
    for (const seq of Array.from(this.frameBuffer.keys())) {
      if (seq < minAllowedSeq) {
        this.frameBuffer.delete(seq);
        dropped++;
      }
    }

    if (dropped > 0) {
      logger.warn(
        'AnimationHandler',
        `Jitter buffer: dropped ${dropped} stale frame(s) older than seq=${minAllowedSeq}`,
      );
    }
  }

  /**
   * Find the lowest buffered sequence at or after minSeq.
   * @internal
   */
  private findLowestBufferedSeqAtOrAfter(minSeq: number): number | null {
    let candidate = Infinity;
    for (const seq of this.frameBuffer.keys()) {
      if (seq >= minSeq && seq < candidate) {
        candidate = seq;
      }
    }
    return candidate === Infinity ? null : candidate;
  }

  /**
   * Begin draining the buffer at 25fps.
   * @internal
   */
  private startBufferDrain(): void {
    this.bufferState = 'draining';
    if (this.bufferNextSeq < 0) {
      let minSeq = Infinity;
      for (const k of this.frameBuffer.keys()) {
        if (k < minSeq) minSeq = k;
      }
      this.bufferNextSeq = minSeq;
    }
    logger.info(
      'AnimationHandler',
      `Jitter buffer: draining (${this.frameBuffer.size} frames buffered)`,
    );
    this.bufferLastDrainTime = performance.now();
    this.drainBufferFrame();
  }

  /**
   * Drain one frame from the buffer and schedule the next drain.
   * Handles missing frames (skip-ahead after maxBufferDelayMs) and starvation.
   * @internal
   */
  private drainBufferFrame(): void {
    this.bufferDrainTimer = null;

    if (this.bufferState !== 'draining') {
      return;
    }

    this.dropStaleBufferedFrames();

    const frame = this.frameBuffer.get(this.bufferNextSeq);

    if (frame) {
      // Expected frame found — render it
      this.renderBufferedFrame(frame);
      this.frameBuffer.delete(this.bufferNextSeq);
      this.bufferNextSeq++;
    } else if (this.frameBuffer.size > 0) {
      // Expected frame missing — check if we should skip ahead to the next in-order frame.
      const nextSeq = this.findLowestBufferedSeqAtOrAfter(this.bufferNextSeq);

      if (nextSeq === null) {
        this.bufferState = 'starved';
        logger.warn(
          'AnimationHandler',
          'Jitter buffer: no in-order frames available, pausing drain',
        );
        return;
      }

      const nextFrame = this.frameBuffer.get(nextSeq)!;
      const waitTime = performance.now() - nextFrame.receivedAt;

      if (waitTime > this.config.maxBufferDelayMs) {
        // Missing frame didn't arrive in time — skip ahead to the next available sequence.
        const gap = Math.max(0, nextSeq - this.bufferNextSeq);
        this.playbackGapCount += gap;
        logger.warn(
          'AnimationHandler',
          `Jitter buffer: skipping ${gap} frame(s) from seq ${this.bufferNextSeq} to ${nextSeq} after ${waitTime.toFixed(1)}ms`,
        );
        this.renderBufferedFrame(nextFrame);
        this.frameBuffer.delete(nextSeq);
        this.bufferNextSeq = nextSeq + 1;
      }
      // else: wait for the missing frame (render nothing this cycle)
    } else {
      // Buffer empty — starvation
      this.bufferState = 'starved';
      logger.warn('AnimationHandler', 'Jitter buffer: starved, pausing drain');
      return; // Don't schedule next drain
    }

    // Schedule next drain with drift correction
    const now = performance.now();
    const nextTarget =
      this.bufferLastDrainTime + AnimationHandler.BUFFER_FRAME_INTERVAL_MS;
    const delay = Math.max(0, nextTarget - now);
    this.bufferLastDrainTime = nextTarget;
    this.bufferDrainTimer = setTimeout(() => this.drainBufferFrame(), delay);
  }

  /**
   * Render a single frame from the buffer and collect playback stats.
   * @internal
   */
  private renderBufferedFrame(frame: BufferedFrame): void {
    if (
      this.lastRenderedFrameSeq >= 0 &&
      frame.seq <= this.lastRenderedFrameSeq
    ) {
      logger.warn(
        'AnimationHandler',
        `Jitter buffer: refusing out-of-order render seq=${frame.seq} (lastRendered=${this.lastRenderedFrameSeq})`,
      );
      return;
    }

    this.renderer.renderFromProtobuf(frame.protobufData);
    this.lastRenderedFrameSeq = frame.seq;
    this.renderedFrameCount++;
    this.logRenderedFrame('buffer', frame.seq);

    // Playback stats
    this.playbackFrameTimestamps.push(performance.now());
    this.playbackFrameCount++;
  }

  /**
   * Flush the jitter buffer, stop the drain loop, and revert to direct mode.
   * @internal
   */
  private flushBuffer(): void {
    this.frameBuffer.clear();
    this.bufferState = 'direct';
    this.bufferNextSeq = -1;
    this.bufferLastDrainTime = 0;
    if (this.bufferDrainTimer !== null) {
      clearTimeout(this.bufferDrainTimer);
      this.bufferDrainTimer = null;
    }
  }

  /**
   * Play a single transition frame and schedule the next one.
   * @internal
   */
  private playTransitionFrame(): void {
    if (
      !this.isPlayingTransition ||
      this.transitionFrameIndex >= this.transitionFrames.length
    ) {
      // Transition complete
      const wasTransitioningToIdle = this.isTransitioningToIdle;
      this.isPlayingTransition = false;
      this.isTransitioningToIdle = false;
      this.transitionFrames = [];
      this.transitionFrameIndex = 0;
      if (this.transitionTimeoutId !== null) {
        clearTimeout(this.transitionTimeoutId);
        this.transitionTimeoutId = null;
      }
      logger.info('AnimationHandler', 'Transition playback complete');

      // If transitioning to idle, start idle animation
      if (wasTransitioningToIdle) {
        logger.info(
          'AnimationHandler',
          'Starting idle animation after transition',
        );
        this.renderer.renderFrame(undefined, true);
        this.logRenderedFrame('idle');
      }
      return;
    }

    if (!this.renderer.isReady()) {
      this.isPlayingTransition = false;
      this.isTransitioningToIdle = false;
      return;
    }

    const frame = this.transitionFrames[this.transitionFrameIndex];
    this.renderer.renderFrame(frame);
    this.logRenderedFrame('transition');
    this.transitionFrameIndex++;

    // Schedule next frame at 25fps (40ms)
    this.transitionTimeoutId = setTimeout(() => {
      if (this.isPlayingTransition) {
        this.playTransitionFrame();
      }
    }, 40);
  }

  /**
   * Emit a per-frame render log for debugging ordering issues.
   * @internal
   */
  private logRenderedFrame(
    source: 'direct' | 'buffer' | 'transition' | 'idle' | 'transition-fallback',
    seq?: number,
    isRecovered?: boolean,
  ): void {
    logger.info(
      'AnimationHandler',
      `Rendered frame: source=${source}, seq=${seq ?? 'n/a'}${isRecovered ? ' [RECOVERED]' : ''}`,
    );
  }
}
